import { Minus, Plus } from 'lucide-react';
import { FieldShell } from './FieldShell';
import type { FieldComponentProps } from './types';

const toNumber = (v: unknown, fallback: number): number => {
  if (typeof v === 'number' && !Number.isNaN(v)) return v;
  if (typeof v === 'string' && v !== '') {
    const n = Number(v);
    return Number.isNaN(n) ? fallback : n;
  }
  return fallback;
};

export const CounterField = ({ field, value, error, onChange }: FieldComponentProps) => {
  const c = field.constraints || {};
  const min = typeof c.min_value === 'number' ? c.min_value : Number.NEGATIVE_INFINITY;
  const max = typeof c.max_value === 'number' ? c.max_value : Number.POSITIVE_INFINITY;
  const start = toNumber(value, typeof c.min_value === 'number' ? c.min_value : 0);

  const clamp = (n: number) => Math.min(Math.max(n, min), max);

  return (
    <FieldShell field={field} error={error}>
      <div className="inline-flex items-center gap-1 rounded-lg border border-rgf-gray-300 bg-white">
        <button
          type="button"
          aria-label="Decrement"
          className="px-3 py-2 text-rgf-gray-600 hover:bg-rgf-gray-50 disabled:opacity-40"
          disabled={start <= min}
          onClick={() => onChange(clamp(start - 1))}
        >
          <Minus className="h-4 w-4" />
        </button>
        <input
          id={field.title}
          type="number"
          inputMode="numeric"
          step={1}
          className="w-20 text-center border-0 focus:ring-0 focus:outline-none"
          value={start}
          min={Number.isFinite(min) ? min : undefined}
          max={Number.isFinite(max) ? max : undefined}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === '') return;
            const n = Number(raw);
            if (!Number.isNaN(n)) onChange(clamp(Math.trunc(n)));
          }}
        />
        <button
          type="button"
          aria-label="Increment"
          className="px-3 py-2 text-rgf-gray-600 hover:bg-rgf-gray-50 disabled:opacity-40"
          disabled={start >= max}
          onClick={() => onChange(clamp(start + 1))}
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>
    </FieldShell>
  );
};
